package net.sergeych.ugdx

import net.sergeych.kiloparsec.command
import net.sergeych.ugdx.shared.*
import net.sergeych.ugdx.shared.api.cmdGetRegisteredImportAddresses
import net.sergeych.ugdx.shared.api.invoke

val cmdAddEthAddress by command<AddEthAddressArgs, Unit>()

/**
 * Ethereum's functionality of the user account in ugdx. All ethereum-connected operations should start from
 * here.
 */
class EthereumAdapter(val account: UserAccount) {

    /**
     * Register ethereum address to be used with the system, e.g. __accept transfers from it__. The signature message
     * should be a JSON structure containing original message obtained with [signatureMessageForAddress] for a given
     * address. Throw errors if it is not possible. IF the address is already tracked, does nothing.
     *
     * @param signature json formatted string with a signed message as provided by external wallet software

     * @throws InvalidOwnershipSignature - for example, if the message is wrong, or a signature is bad or signed with
     *              some different address
     *
     * @throws AddressInUseError if the given address is already associated with some (also this one) user account.
     */
    suspend fun addSignedAddress(name: String, address: String, message: String, signature: String) {
        try {
            val formattedMessage = """
                {
                  "address": "$address",
                  "msg": "$message",
                  "sig": "$signature",
                  "version":"2"
                }
                """.trimIndent()
            cmdAddEthAddress.invoke(AddEthAddressArgs(name, address, formattedMessage))
        } catch (x: ApiException) {
            when (ErrorCode.from(x.code)) {
                ErrorCode.ADDRESS_IN_USE -> throw AddressInUseError("this address is already tracked by other user")
                ErrorCode.INVALID_ETH_SIGNATURE -> throw InvalidOwnershipSignature("bad signature")
                ErrorCode.INVALID_ETH_SIGNED_MESSAGE -> throw InvalidOwnershipSignedMessage("bad (changed?) message")
                else -> throw x
            }
        }
    }

    /**
     * Get the list of ethereum addresses that are associated with this account iseomg [addSignedAddress].
     */
    @Suppress("unused")
    suspend fun getRegisteredAddresses(): List<String> {
        return cmdGetRegisteredImportAddresses(Unit).addresses
    }

    companion object {
        /**
         * Registration owner ethereum address, step 1: obtain a message to sign with owned address. The
         * signature is performed externally in some wallet app like metamask. The message should be signed
         * as is without alterations!
         *
         * @param fromAddress address which ownership is to be confirmed, the message should be signed using this
         *              address private key.
         * @param realm what network to use (ethereum | rinkeby).
         * @return message text to be signed with eth specified address
         */
        suspend fun signatureMessageForAddress(
            fromAddress: String,
            realm: Currency.Realm = Currency.Realm.ETHEREUM
        ): String {
            val r: GetEthSignatureMessageResult = cmdGetEthSignatureMessage(
                GetEthSignatureMessageArgs(fromAddress, realm)
            )
            return r.message
        }
    }
}



